import {
  blockItemsResponse,
  blocksListResponse,
  photoReviewResponse,
  reportDetailResponse,
  reportPhotosResponse,
  type BlockItemsResponse,
  type BlocksListResponse,
  type PhotoReviewRequest,
  type PhotoReviewResponse,
  type ReportDetailResponse,
  type ReportPhotosResponse,
} from "@bitacorapro/api-types";
import { request } from "../client";

// GET /reports/:id/photos — the lazy per-visit photo feed behind
// DetalleTienda.tsx's .vgal gallery. Its own module (not folded into
// facility-dashboard.ts) because it's keyed by reportId, not the
// dashboard-wide aggregate, matching the assets.ts/checklist-templates.ts
// split by resource rather than by screen. Ingress parse per ADR-0021/D6.
export const reportsApi = {
  getPhotos: async (reportId: string): Promise<ReportPhotosResponse> => {
    const json = await request<unknown>("GET", "core", `/reports/${reportId}/photos`);
    return reportPhotosResponse.parse(json);
  },
  // POST /reports/:id/photo-reviews (ADR-0169) — one photo's human
  // decision. The caller owns the Idempotency-Key: one per decision, so a
  // «Reintentar» of the same decision replays it instead of writing twice.
  reviewPhoto: async (
    reportId: string,
    body: PhotoReviewRequest,
    idempotencyKey: string
  ): Promise<PhotoReviewResponse> => {
    const json = await request<unknown>("POST", "core", `/reports/${reportId}/photo-reviews`, {
      body,
      headers: { "Idempotency-Key": idempotencyKey },
    });
    return photoReviewResponse.parse(json);
  },
  // GET /reports/:id — the closed-report view's (/reportes/:id) header
  // and seal source: the report row plus its frozen findings/evidence.
  getDetail: async (reportId: string): Promise<ReportDetailResponse> => {
    const json = await request<unknown>("GET", "core", `/reports/${reportId}`);
    return reportDetailResponse.parse(json);
  },
  // GET /reports/:id/blocks — per-bloque derived state (label, totals).
  // The same payload the PWA's revisión screens read; here it feeds the
  // "Resultado por sección" rows.
  getBlocks: async (reportId: string): Promise<BlocksListResponse> => {
    const json = await request<unknown>("GET", "core", `/reports/${reportId}/blocks`);
    return blocksListResponse.parse(json);
  },
  // GET /reports/:id/blocks/:areaId/items — one call per bloque, so a
  // single failed section can be retried without refetching the rest.
  getBlockItems: async (reportId: string, areaId: string): Promise<BlockItemsResponse> => {
    const json = await request<unknown>(
      "GET",
      "core",
      `/reports/${reportId}/blocks/${areaId}/items`
    );
    return blockItemsResponse.parse(json);
  },
};
